/**
 * Popup Level Manager
 *
 * Singleton composable that tracks all currently open popups and
 * automatically manages their stacking level (z-index offset).
 *
 * Registration order determines the level:
 *   - 1st popup opened → level 1
 *   - 2nd popup opened → level 2  (renders on top)
 *   - etc.
 */
import {reactive, computed} from 'vue'

interface PopupEntry {
  id: string
}

// Module-level singleton state – shared across all PopupBase instances
const state = reactive({
  openPopups: [] as PopupEntry[]
})

/**
 * Returns true if the given popup is the topmost (last opened) popup.
 * Useful to decide whether the close button / backdrop should be interactive.
 */
function isTopLevel(id: string): boolean {
  if (state.openPopups.length === 0) {
    return false
  }
  return state.openPopups[state.openPopups.length - 1]?.id === id
}

/**
 * Register a popup as open.
 * Should be called when `show` becomes true.
 */
function registerPopup(id: string): void {
  if (!state.openPopups.some((p) => p.id === id)) {
    state.openPopups = [...state.openPopups, {id}]
  }
}

/**
 * Unregister a popup.
 * Should be called when `show` becomes false or the component is unmounted.
 */
function unregisterPopup(id: string): void {
  const index = state.openPopups.findIndex((p) => p.id === id)
  if (index !== -1) {
    state.openPopups.splice(index, 1)
  }
}

/**
 * Returns the 1-based level for the given popup id.
 * Returns 0 if the popup is not currently registered (closed).
 */
function getLevel(id: string): number {
  const index = state.openPopups.findIndex(p => p.id === id)
  return index === -1 ? 0 : index + 1
}

export function usePopupLevelManager() {
  /** Total number of currently open popups */
  const popupCount = computed(() => state.openPopups.length)
  const openPopups = computed(() => [...state.openPopups])

  return {
    popupCount,
    openPopups,
    registerPopup,
    unregisterPopup,
    getLevel,
    isTopLevel
  }
}
